import { TextInput } from 'react-native';
import { useState, useCallback, useMemo } from 'react';

const hiddenStyle = {
  position: 'absolute',
  left: -9999,
  width: 1,
  height: 1,
  opacity: 0,
};

/**
 * This component is a honeypot input field designed to trap bots. It is hidden from users and screen readers, and if a bot fills it out, it will trigger an analytics event and a callback function.
 * 
 * @param {string} name - A unique identifier for the honeypot field, used in analytics and callback.
 * @param {function} analytics - A function to log analytics events when the honeypot is triggered.
 * @param {function} callback - A function to call when the honeypot is triggered, receiving the setName as an argument.
 * @returns 
 */
const HoneypotInput = ({ name = 'honeypot', analytics, callback }) => {
  const setName = useMemo(() => `sugar_${name}`, [name]);
  const [value, setValue] = useState('');
  const [hasTriggered, setHasTriggered] = useState(false);

  const handleChange = useCallback((text) => {
    setValue(text);
    if (text && !hasTriggered) {
      setHasTriggered(true);
      analytics?.('honeypot_triggered', { name: `${setName}_touched` });
      callback?.(setName);
    }
  }, [hasTriggered, setName, analytics, callback]);
  
  return (
    <TextInput
      value={value}
      name={name}
      onChangeText={handleChange}
      style={hiddenStyle}
      accessibilityElementsHidden={true}
      importantForAccessibility="no-hide-descendants"
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
    />
  );
};

HoneypotInput.displayName = 'HoneypotInput';

export default HoneypotInput;